import { useMemo } from 'react';
import { useSuperAdminData } from '../../dashboard/hooks/useSuperAdminData';
import type { Project } from '../../projects/types';

/**
 * Estadísticas consolidadas para la Consola Territorial del Superadmin.
 */
export interface OverviewStats {
  /** Total de proyectos en plataforma */
  totalProjects: number;
  /** Total de tenants/clientes registrados */
  totalClients: number;
  /** Total de usuarios en plataforma */
  totalUsers: number;
  /** Tenants con status 'active' */
  activeClients: number;
  /** Usuarios con isActive = true */
  activeUsers: number;
  /** Proyectos con módulo 'urban-network' activo */
  projectsWithNetwork: number;
  /** Fecha ISO del proyecto más recientemente modificado */
  lastActivity: string | null;
}

/**
 * Hook de estadísticas para la Consola Territorial Superadmin.
 *
 * @remarks
 * Reutiliza `useSuperAdminData` sin duplicar llamadas a la API.
 * Los tres endpoints (projects, tenants, users) se cargan en paralelo.
 *
 * Expone un shape orientado al overview global, diferente al shape
 * del `SuperAdminDashboard` operativo.
 *
 * Derivaciones:
 * - `projectsWithNetwork`: proyectos con módulo 'urban-network' en activeModules.
 *   Proxy razonable de "proyecto con cobertura de red territorial".
 *
 * @example
 * ```tsx
 * const { stats, loading, error } = useSuperadminOverviewStats();
 * ```
 */
export function useSuperadminOverviewStats(): {
  stats: OverviewStats;
  projects: Project[];
  loading: boolean;
  error: string | null;
} {
  const { projects, stats: dashStats, loading, error } = useSuperAdminData();

  const stats: OverviewStats = useMemo(
    () => ({
      totalProjects: dashStats.totalProjects,
      totalClients: dashStats.totalTenants,
      totalUsers: dashStats.totalUsers,
      activeClients: dashStats.activeTenants,
      activeUsers: dashStats.activeUsersCount,
      projectsWithNetwork: projects.filter(p => p.activeModules?.includes('urban-network')).length,
      lastActivity: dashStats.lastActivity,
    }),
    [dashStats, projects]
  );

  return { stats, projects, loading, error };
}
